import { useEffect, useState } from 'react';

import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';

import { Maybe } from '@/types';
import { getBlogListByBrand } from '@/services/getBlogListByBrand';
import { BlogDetailsPage, BlogDetailsPageCollection, BlogList as TBlogList } from '@/types/generated';
import { logWarning } from '@/utils/miscUtils';
import { useAppContext } from '@/context/AppContextProvider.ctx';
import { getNumberOfBlogs } from '@/utils';
import TAGLink from '@/components/TAGLink';

import BlogListPageDefault from './BlogListPageDefault/BlogListPageDefault';
import { ICardDesignType } from './BlogListItem';
import {
	blogListTitlerContainerStyles,
	blogListHeaderStyles,
	blogListTitleStyles,
} from './BlogListPageDefault/BlogListPageDefault.styles';

export type BlogListResponse = {
	blogDetailsPageCollection: BlogDetailsPageCollection;
	total: number;
	skip: number;
	limit: number;
};

export interface IBlogList extends TBlogList {
	/**
	 * Used while fetching the blog list.
	 *
	 * @type {Boolean}
	 * @memberof IBlogList
	 */
	preview?: boolean;
	cardDesignType?: ICardDesignType;
	startIndexBlogList: number;
	setStartIndexBlogList: (startIndex: number) => void;
	categoryName?: string;
	subCategoryName?: string;
	tags?: Array<string>;
}

export const getPaginationPageIndex = (pageNumber: number, countPerPage: number) => {
	return (pageNumber - 1) * countPerPage;
};

const BlogListTitle = ({ title }: { title?: Maybe<string> }) => {
	return (
		<>
			{title && (
				<Box sx={blogListTitlerContainerStyles}>
					<Typography
						sx={blogListTitleStyles}
						component="h3"
						variant="overline"
						color="white"
						data-test-id="blog_list_title"
					>
						{title}
					</Typography>
				</Box>
			)}
		</>
	);
};

export default function BlogList({
	title,
	displayType,
	cardDesignType,
	brand,
	buttonLabel,
	loadMoreButtonLabel,
	preview = false,
	countAcrossDesktop,
	countAcrossMobile,
	hasPagination = true,
	featuredBlogsListCollection,
	startIndexBlogList = 0,
	setStartIndexBlogList,
	category,
	categoryName,
	subCategoryName,
	tagsFilter,
	tags,
}: Readonly<IBlogList>) {
	const isFeatured = displayType === 'Featured';
	const isTagType = displayType === 'Tag';

	const numberOfBlogs = getNumberOfBlogs(displayType as string);
	const [state, setState] = useState<{
		totalBlogs: number;
		displayShowMoreButton: boolean;
		blogsList: Array<BlogDetailsPage>;
	}>({
		totalBlogs: 0,
		blogsList: [],
		displayShowMoreButton: false,
	});
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

	const { environment, config } = useAppContext();
	const contentfulGraphqlURL =
		environment === 'prod'
			? config.services.prod.CONTENTFUL_GRAPHQL_URL
			: config.services.nonprod.CONTENTFUL_GRAPHQL_URL;

	// brand name comes from blog list content itself or take it from context
	const brandNameForQuery = brand?.title ?? config.name;

	const getStateReset = () => ({
		...state,
		blogsList: [],
		displayShowMoreButton: false,
		totalBlogs: 0,
	});
	let filterByTags: Array<string> | undefined;
	if (isTagType) {
		filterByTags = tagsFilter?.length ? (tagsFilter as Array<string>) : tags;
	} else {
		filterByTags = undefined;
	}
	useEffect(() => {
		const featuredBlogsList = featuredBlogsListCollection?.items as BlogDetailsPage[];
		if (isFeatured) {
			setState({
				...state,
				blogsList: featuredBlogsList,
				displayShowMoreButton: false,
				totalBlogs: featuredBlogsListCollection?.items.length ?? 2,
			});
			return;
		}
		getBlogListByBrand(
			numberOfBlogs,
			startIndexBlogList,
			brandNameForQuery,
			contentfulGraphqlURL,
			preview,
			category?.name as string,
			subCategoryName,
			filterByTags
		)
			.then((blogsListResponse: BlogListResponse | undefined) => {
				if (blogsListResponse) {
					const blogDetailsPageItems = blogsListResponse.blogDetailsPageCollection
						?.items as Array<BlogDetailsPage>;
					const totalBlogs = blogsListResponse.blogDetailsPageCollection?.total ?? 0;
					const blogDetailsList =
						startIndexBlogList === 0 ? blogDetailsPageItems : state.blogsList.concat(blogDetailsPageItems);
					setState({
						...state,
						blogsList: blogDetailsList,
						displayShowMoreButton: displayType === 'Blog List Page' && blogDetailsList.length < totalBlogs,
						totalBlogs,
					});
				}
				return blogsListResponse;
			})
			.catch((error) => {
				// Handle any errors that occurred during the API call.
				// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
				logWarning(`Error fetching blogs list by brand error: ${error}`);
				setState(getStateReset());
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [startIndexBlogList, categoryName, subCategoryName]);

	const handleShowMoreClick = () => {
		setStartIndexBlogList(startIndexBlogList + numberOfBlogs);
		setState({
			...state,
			displayShowMoreButton: false,
		});
	};

	const handlePaginationChangeEvent = (e: React.ChangeEvent<unknown>, page: number) => {
		const indexByPage = getPaginationPageIndex(page, numberOfBlogs);
		window.scrollTo({ top: 0 });
		setState(getStateReset());

		getBlogListByBrand(
			numberOfBlogs,
			indexByPage,
			brandNameForQuery,
			contentfulGraphqlURL,
			preview,
			categoryName,
			subCategoryName
		)
			.then((blogsListResponse: BlogListResponse | undefined) => {
				if (blogsListResponse) {
					setState({
						...state,
						blogsList: blogsListResponse.blogDetailsPageCollection?.items as Array<BlogDetailsPage>,
					});
					window.scrollTo({ top: 0, behavior: 'smooth' });
				}
				return blogsListResponse;
			})
			.catch((error) => {
				// Handle any errors that occurred during the API call.
				// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
				logWarning(`Error fetching blogs list by brand error: ${error}`);
				setState(getStateReset());
			});

		setStartIndexBlogList(0);
	};

	const viewAllLink = () => {
		const blogRootPath = config.features.blogs.root;
		return (
			<TAGLink
				linkSx={{
					padding: '1rem 1.5rem', // 16px 24px
					fontSize: '0.875rem', // 14px
					fontWeight: 700,
					letterSpacing: '0.1875rem', // 3px
					fontFamily: 'Messina Sans',
					color: '#FFFFFF',
					border: '0.0625rem solid', // 1px
					borderRadius: '1.625rem', // 26px
					marginRight: { xs: 0, md: '5%' },
				}}
				href={blogRootPath}
			>
				VIEW ALL
			</TAGLink>
		);
	};

	return (
		<Box sx={{ width: '100%', background: '#002044' }}>
			<Box sx={blogListHeaderStyles} data-test-id="blog_list_title_wrapper">
				<BlogListTitle title={title} />
				{displayType === 'One Row' && !isSmallScreen ? viewAllLink() : null}
			</Box>
			<BlogListPageDefault
				totalBlogs={state.totalBlogs}
				blogsList={state.blogsList}
				buttonLabel={buttonLabel as string}
				loadMoreButtonLabel={loadMoreButtonLabel as string}
				displayShowMoreButton={state.displayShowMoreButton}
				handleShowMoreClick={handleShowMoreClick}
				handlePaginationChangeEvent={handlePaginationChangeEvent}
				cardDesignType={cardDesignType}
				countAcrossDesktop={countAcrossDesktop as number}
				countAcrossMobile={countAcrossMobile as number}
				hasPagination={hasPagination}
				limit={12}
				displayType={displayType}
			/>
			{displayType === 'One Row' && isSmallScreen ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', paddingBottom: '40px' }}>
					{viewAllLink()}
				</Box>
			) : null}
		</Box>
	);
}
